import { LANGUAGE, tt } from "../../utils/i18n/i18n";
import { Elements } from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import { AxiosForLegacyBackend } from "../../utils/helpers/AxiosForLegacyBackend";
import { AxiosError } from "axios";
import type BillDTO from "@tootsweet/model/datatransfer/BillDTO";
import CheckoutFormInner from "./CheckoutFormInner";
import stripePromise from "../stripePromise";
import { useUrlQuery } from "../../utils/hooks/useUrlQuery";
import PaymentFormAPI from "./api";
import { useLocation } from "react-router-dom";
import ChooseCardForm from "./ChooseCardForm";
import { type SaasCommitment } from "../../model/SaasTypes";
import { OVERRIDE_APPERANCE } from "./utils/OVERRIDE_APPERANCE";
import { OVERRIDE_FONTS } from "./utils/OVERRIDE_FONTS";
import moment from "moment/moment";
import {
  setLoading,
  useCurrencySelector,
  useSubscriptionPlansSelector
} from "../../redux/actions/app";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import { useDispatch } from "react-redux";
import TSUtils from "../../utils/helpers/TSUtils";
import getErrorMessage, {
  getErrorTitle
} from "../../utils/error_handling/getErrorMessage";
import NiceModal from "@ebay/nice-modal-react";
import PostPaymentModal from "../modal/ConfirmPayment/PostPaymentModal";
import PaymentCard from "../../model/PaymentCard";

interface Props {
  businessUserId: string;
  posaDeliveryIds: string[];
  bill?: BillDTO | null;
  amount: number;
  onFinished: () => void;
  doNotRedirect?: boolean;
  handlePrev?: () => void;
  isSaasSubscriptionForm: boolean;
  saasPlanId?: string | null;
  saasCommitment?: SaasCommitment | null;
  afterStripePaymentConfirmed?: () => Promise<void>;
  promoCode?: string;
  promoCode2?: string;
  startTs?: number;
  onPayStarted?: () => void;
  isFirstMonthFree?: boolean;
  firstPaymentDate?: Date;
  isStartDateInFuture?: boolean;
}

const STEP_0_CHOOSE_CARD = 0;
const STEP_1_INSERT_CARD = 1;

const processedPaymentIntentCache: any = {};

export default function CheckoutFormOuter(props: Props) {
  const dispatch = useDispatch();
  const allSubscriptionPlans = useSubscriptionPlansSelector();
  const currency = useCurrencySelector();
  const location = useLocation();
  const query = useUrlQuery();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [clientSecret, setClientSecret] = useState("");
  const [step, setStep] = useState(STEP_0_CHOOSE_CARD);
  const [paymentCards, setPaymentCards] = useState<PaymentCard[] | null>(null);
  const [isSetupIntent, setIsSetupIntent] = useState(false);

  const shouldUseSetupIntent = useMemo(() => {
    return !!props.isFirstMonthFree || !!props.isStartDateInFuture;
  }, [props.isFirstMonthFree, props.isStartDateInFuture]);

  const {
    payment_intent_id,
    payment_intent_client_secret,
    redirect_status,
    billId,
    setup_intent_id,
    setup_intent_client_secret
  } = React.useMemo(() => {
    return {
      payment_intent_id: query.get("payment_intent"),
      payment_intent_client_secret: query.get("payment_intent_client_secret"),
      redirect_status: query.get("redirect_status"),
      billId: query.get("billId"),
      setup_intent_id: query.get("setup_intent"),
      setup_intent_client_secret: query.get("setup_intent_client_secret")
    };
  }, [query]);

  const handleSuccessPostPayment = async (
    payment_intent_id: string | undefined | null,
    setup_intent_id: string | undefined | null
  ) => {
    if (!accountToDisplay?.token) {
      return;
    }

    if (
      (!payment_intent_id && !setup_intent_id && !shouldUseSetupIntent) ||
      (payment_intent_id && processedPaymentIntentCache[payment_intent_id]) ||
      (setup_intent_id && processedPaymentIntentCache[setup_intent_id])
    ) {
      return;
    }

    if (payment_intent_id) {
      processedPaymentIntentCache[payment_intent_id] = true;
    }

    if (setup_intent_id) {
      processedPaymentIntentCache[setup_intent_id] = true;
    }

    const finalBillId = props.bill?.id || billId || "";

    if (finalBillId) {
      await PaymentFormAPI.afterStripePayment(
        props.businessUserId,
        finalBillId,
        props.posaDeliveryIds,
        payment_intent_id || "",
        accountToDisplay.token
      );
    } else if (props.isSaasSubscriptionForm) {
      if (!props.promoCode2 && (!props.saasPlanId || !props.saasCommitment)) {
        alert(tt("checkoutFormValidationError"));
        dispatch(setLoading(false));
        return;
      }

      if (props.afterStripePaymentConfirmed != null) {
        await props.afterStripePaymentConfirmed();
      }

      try {
        await PaymentFormAPI.completeSubscription(
          props.businessUserId,
          props.saasPlanId,
          props.saasCommitment,
          payment_intent_id || "",
          props.startTs ?? moment().unix(),
          props.promoCode,
          props.promoCode2,
          accountToDisplay.token,
          setup_intent_id || ""
        );
      } catch (err) {
        dispatch(setLoading(false));

        let errorMsg = tt("generic_error");
        let errorTitle = getErrorTitle();

        if (TSUtils.isAxiosError(err)) {
          const axiosRes = (err as AxiosError).response;

          if (axiosRes?.data?.code) {
            errorMsg = getErrorMessage(axiosRes?.data?.code);
            errorTitle = getErrorTitle(axiosRes?.data?.code);
          }
        }

        await NiceModal.show(PostPaymentModal, {
          headerText: errorTitle,
          bodyText: errorMsg,
          buttonText: tt("ok")
        });

        return;
      }
    } else {
      alert(tt("unsupportedPaymentError"));
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    queryParams.delete("payment_intent");
    queryParams.delete("payment_intent_client_secret");
    queryParams.delete("redirect_status");
    queryParams.delete("billId");
    queryParams.delete("setup_intent");
    queryParams.delete("setup_intent_client_secret");

    props.onFinished();
  };

  const postSetupIntent = async () => {
    if (
      !allSubscriptionPlans?.length ||
      !setup_intent_id ||
      !setup_intent_client_secret ||
      !redirect_status
    ) {
      return;
    }

    const stripe = await stripePromise;

    if (stripe == null) {
      alert(tt("api_error"));
      return;
    }

    const { setupIntent } = await stripe.retrieveSetupIntent(
      setup_intent_client_secret
    );

    if (!setupIntent?.id) {
      return;
    }

    switch (setupIntent?.status) {
      case "succeeded":
        await handleSuccessPostPayment("", setup_intent_id);
        break;

      case "processing":
        alert(tt("payment_transaction_ongoing"));
        break;

      case "requires_payment_method":
        alert(tt("card_error_debit_transaction_frequency_exceeded"));
        break;
      default:
        alert(tt("somethingWentWrong"));
        break;
    }

    dispatch(setLoading(false));
  };

  const postPayment = async () => {
    if (
      !allSubscriptionPlans?.length ||
      !payment_intent_id ||
      !payment_intent_client_secret ||
      !redirect_status
    ) {
      return;
    }

    const stripe = await stripePromise;

    if (stripe == null) {
      alert(tt("api_error"));
      return;
    }

    const { paymentIntent } = await stripe.retrievePaymentIntent(
      payment_intent_client_secret
    );

    if (!paymentIntent?.id) {
      return;
    }

    switch (paymentIntent?.status) {
      case "succeeded":
        await handleSuccessPostPayment(payment_intent_id, undefined);
        break;

      case "processing":
        alert(tt("payment_transaction_ongoing"));
        break;

      case "requires_payment_method":
        alert(
          `${tt("card_error_debit_transaction_frequency_exceeded")} (${
            paymentIntent.id
          }, requires_payment_method)`
        );
        break;
      default:
        alert(tt("somethingWentWrong"));
        break;
    }

    dispatch(setLoading(false));
  };

  const displayAppropriateForm = () => {
    if (paymentCards?.length === 0) {
      void goToInsertCard();
    } else {
      setStep(STEP_0_CHOOSE_CARD);
    }
  };

  const fetchCards = async () => {
    dispatch(setLoading(true));
    const res = await AxiosForLegacyBackend("/payment/cards2", {
      method: "get",
      params: {
        businessUserId: props.businessUserId
      }
    });

    dispatch(setLoading(false));
    setPaymentCards(res.data);
    displayAppropriateForm();
  };

  const refreshIntent = async () => {
    try {
      dispatch(setLoading(true));

      let res;

      if (shouldUseSetupIntent) {
        res = await PaymentFormAPI.createStripeSetupIntent(
          props.businessUserId
        );
      } else if (props.bill == null) {
        res = await PaymentFormAPI.createStripePaymentIntentWithoutBill(
          props.businessUserId,
          props.amount
        );
      } else {
        res = await PaymentFormAPI.createStripePaymentIntent(
          props.businessUserId,
          props.posaDeliveryIds,
          props.bill.id
        );
      }

      if (!res.clientSecret) {
        alert(tt("paymentServerError"));
        return;
      }

      setClientSecret(res.clientSecret);
    } catch (err) {
      alert(tt("error"));
      console.error(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const goToInsertCard = async () => {
    setStep(STEP_1_INSERT_CARD);
  };

  const formattedButtonText = useMemo(() => {
    if (accountToDisplay) {
      if (props.bill) {
        return tt("pay_amount", {
          amount: TSUtils.formatAmount(
            props.amount,
            accountToDisplay.curSubUnits,
            false,
            accountToDisplay.currency
          )
        });
      }

      if (accountToDisplay) {
        return tt("pay_amount_subscription");
      }
    }

    if (props.isFirstMonthFree && props.firstPaymentDate) {
      return tt("subscribe_and_pay_on_date");
    }

    return "???";
  }, [
    accountToDisplay,
    props.bill,
    props.amount,
    currency,
    props.isFirstMonthFree,
    props.firstPaymentDate
  ]);

  const buttonSubText = useMemo(() => {
    if (!props.isStartDateInFuture) {
      return "";
    }
    return tt("first_payment_will_take_place");
  }, [
    accountToDisplay,
    props.bill,
    props.amount,
    currency,
    props.isFirstMonthFree,
    props.firstPaymentDate,
    props.isStartDateInFuture
  ]);

  const STRIPE_OPTIONS = useMemo(() => {
    return {
      fonts: OVERRIDE_FONTS,
      // @ts-ignore
      clientSecret: clientSecret || "",
      loader: "always",
      // @ts-ignore
      locale: LANGUAGE,
      // @ts-ignore
      appearance: OVERRIDE_APPERANCE
    };
  }, [clientSecret]);

  useEffect(() => {
    void fetchCards();
  }, []);

  useEffect(() => {
    void postPayment();
  }, [
    allSubscriptionPlans,
    payment_intent_id,
    payment_intent_client_secret,
    redirect_status
  ]);

  useEffect(() => {
    void postSetupIntent();
  }, [
    allSubscriptionPlans,
    setup_intent_id,
    setup_intent_client_secret,
    redirect_status
  ]);

  useEffect(() => {
    setIsSetupIntent(shouldUseSetupIntent);
    displayAppropriateForm();
  }, [shouldUseSetupIntent, paymentCards]);

  useEffect(() => {
    if (!props.amount) {
      return;
    }
    void refreshIntent();
  }, [
    props.amount,
    props.isFirstMonthFree,
    props.isStartDateInFuture,
    props.bill?.id,
    props.businessUserId
  ]);

  if (
    payment_intent_id ||
    payment_intent_client_secret ||
    setup_intent_id ||
    setup_intent_client_secret ||
    redirect_status
  ) {
    return null;
  }

  return (
    <>
      {step === STEP_0_CHOOSE_CARD && (
        <ChooseCardForm
          paymentCards={paymentCards}
          businessUserId={props.businessUserId}
          posaDeliveryIds={props.posaDeliveryIds}
          amount={props.bill != null ? props.bill.amount : props.amount}
          goToInsertCard={goToInsertCard}
          afterPaymentSuccessful={handleSuccessPostPayment}
          saasCommitment={props.saasCommitment}
          onPayStarted={props.onPayStarted}
          formattedButtonText={formattedButtonText}
          buttonSubText={buttonSubText}
          isFirstMonthFree={props.isFirstMonthFree}
          shouldUseSetupIntent={shouldUseSetupIntent}
        />
      )}

      {!!clientSecret && step === STEP_1_INSERT_CARD && (
        <Elements
          key={`${isSetupIntent ? "setup" : "payment"}-${clientSecret}`}
          stripe={stripePromise}
          //@ts-ignore
          options={STRIPE_OPTIONS}
        >
          <CheckoutFormInner
            amount={props.bill != null ? props.bill.amount : props.amount}
            handlePrev={props.handlePrev}
            isSaasSubscriptionForm={props.isSaasSubscriptionForm}
            saasCommitment={props.saasCommitment}
            saasPlanId={props.saasPlanId}
            isForPromoCode2={!!props.promoCode2}
            bill={props.bill}
            formattedButtonText={formattedButtonText}
            buttonSubText={buttonSubText}
            isFirstMonthFree={props.isFirstMonthFree}
            isSetupIntent={isSetupIntent}
          />
        </Elements>
      )}
    </>
  );
}
